import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { Mail, RefreshCw, Unplug, Loader2, AlertTriangle, CheckCircle2, Clock, Timer, Calendar } from 'lucide-react';
import { useGmailStore } from '../../store/gmailStore';
import { useAuthStore } from '../../store/authStore';
import { EmailComposeModeControl } from './EmailComposeModeControl';
import type { SyncStatus } from '../../types';

const STATUS_CONFIG: Record<SyncStatus, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  CONNECTED: {
    label: 'Connected',
    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: CheckCircle2,
  },
  SYNCING: {
    label: 'Syncing',
    color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    icon: RefreshCw,
  },
  ERROR: {
    label: 'Error',
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: AlertTriangle,
  },
  DISCONNECTED: {
    label: 'Disconnected',
    color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400',
    icon: Unplug,
  },
};

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const GmailConnectionCard = () => {
  const {
    connected,
    googleEmail,
    syncStatus,
    syncError,
    lastSyncAt,
    calendarSyncEnabled,
    needsCalendarReconnect,
    isLoading,
    isConnecting,
    isDisconnecting,
    error,
    fetchStatus,
    initiateConnect,
    initiateCalendarSync,
    disconnect,
  } = useGmailStore();
  const user = useAuthStore((s) => s.user);
  const setEmailComposeMode = useAuthStore((s) => s.setEmailComposeMode);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleDisconnect = () => {
    if (!confirm('Are you sure you want to disconnect Gmail? Email sync will stop.')) {
      return;
    }
    disconnect();
  };

  const statusConfig = syncStatus ? STATUS_CONFIG[syncStatus] : null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Gmail Integration</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Automatically sync emails with CRM contacts
          </p>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : connected && syncStatus && syncStatus !== 'DISCONNECTED' ? (
        /* Connected state */
        <div className="space-y-4">
          {/* Status badge */}
          {statusConfig && (
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                <statusConfig.icon className={`w-3.5 h-3.5 ${syncStatus === 'SYNCING' ? 'animate-spin' : ''}`} />
                {statusConfig.label}
              </span>
            </div>
          )}

          {/* Connection details */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Connected email</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{googleEmail}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">Last sync</span>
              {lastSyncAt ? (
                <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {formatDateTime(lastSyncAt)}
                </span>
              ) : (
                <span className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <Timer className="w-3.5 h-3.5" />
                  Pending
                </span>
              )}
            </div>
          </div>

          {/* Sync pending notice */}
          {!lastSyncAt && syncStatus === 'CONNECTED' && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-start gap-2">
                <Timer className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Sync pending — initial email sync will start within 5 minutes.
                  Historical emails from the last 90 days will be imported automatically.
                </p>
              </div>
            </div>
          )}

          {/* Sync error message */}
          {syncStatus === 'ERROR' && syncError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">Sync error</p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">{syncError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Calendar Sync */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Google Calendar Sync
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Sync scheduled activities to your Google Calendar
                  </p>
                </div>
              </div>
              {needsCalendarReconnect ? (
                <button
                  onClick={initiateCalendarSync}
                  disabled={isConnecting}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {isConnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Reconnect for Calendar
                </button>
              ) : calendarSyncEnabled ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  Enabled
                </span>
              ) : (
                <button
                  onClick={initiateCalendarSync}
                  disabled={isConnecting}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {isConnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calendar className="w-3.5 h-3.5" />}
                  Enable Calendar Sync
                </button>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            {syncStatus === 'ERROR' ? (
              <button
                onClick={initiateConnect}
                disabled={isConnecting}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {isConnecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Reconnect Gmail
              </button>
            ) : null}
            <button
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {isDisconnecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Unplug className="w-4 h-4" />
              )}
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        /* Not connected state */
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Connect your Google account to log email automatically and add scheduled activities to your Google Calendar.
          </p>
          <button
            onClick={initiateConnect}
            disabled={isConnecting}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {isConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Connect Google
          </button>
        </div>
      )}

      {/* Send mode: compose in the CRM vs. open in Gmail (app or web) vs. open
          the rep's own mail app (mailto). Always visible (not just while
          connected) so a rep on COMPOSER can see the other two options are
          there — just gated until they connect Gmail. */}
      {!isLoading && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <EmailComposeModeControl
            mode={user?.emailComposeMode ?? 'COMPOSER'}
            connected={connected}
            onChange={async (m) => {
              const r = await setEmailComposeMode(m);
              if ('needsGmail' in r) {
                toast.error('Connect Gmail first — keeps your sent emails logged.');
                return;
              }
              toast.success(
                m === 'MAILTO'
                  ? 'Emails will open in your mail app'
                  : m === 'GMAIL'
                    ? 'Emails will open in Gmail'
                    : 'Emails will compose in the CRM',
              );
            }}
          />
        </div>
      )}
    </div>
  );
};
